import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { ChevronRight, BarChart3, Calendar, Users, Target, TrendingUp, Trophy, Newspaper, Play, Tv } from 'lucide-react';
import HomeScoreboard from '../components/HomeScoreboard';
import LiveChat from '../components/LiveChat';
import AdSenseAd from '../components/AdSenseAd';
import ESPNNewsSection from '../components/ESPNNewsSection';
import ElectionHub from '../components/ElectionHub';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Check if scoreboard should be visible based on day/time (America/New_York timezone)
// VISIBLE: Friday 6:00 AM ET → Monday 6:00 AM ET
// HIDDEN: Monday 6:00 AM ET → Friday 6:00 AM ET
const shouldShowScoreboard = (displayMode = 'automatic') => {
  // Handle manual overrides first
  if (displayMode === 'always_show') return true;
  if (displayMode === 'always_hide') return false;
  
  // Automatic mode - check based on day/time in America/New_York
  const now = new Date();
  const etOptions = { timeZone: 'America/New_York', hour12: false };
  const etDateStr = now.toLocaleString('en-US', { ...etOptions, weekday: 'short', hour: '2-digit' });
  
  // Get ET day and hour
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    weekday: 'short',
    hour: 'numeric',
    hour12: false
  });
  const parts = formatter.formatToParts(now);
  const dayName = parts.find(p => p.type === 'weekday')?.value || '';
  const hour = parseInt(parts.find(p => p.type === 'hour')?.value || '0', 10);
  
  // Schedule:
  // Friday 6:00 AM through Sunday 11:59 PM = VISIBLE
  // Monday 12:00 AM through 5:59 AM = VISIBLE
  // Monday 6:00 AM through Thursday 11:59 PM = HIDDEN
  // Friday 12:00 AM through 5:59 AM = HIDDEN
  
  const day = dayName.toLowerCase().slice(0, 3); // 'fri', 'sat', 'sun', 'mon', 'tue', 'wed', 'thu'
  
  if (day === 'sat' || day === 'sun') {
    return true; // Saturday and Sunday always visible
  }
  
  if (day === 'fri') {
    return hour >= 6; // Friday: visible from 6 AM onwards
  }
  
  if (day === 'mon') {
    return hour < 6; // Monday: visible until 6 AM
  }
  
  // Tuesday, Wednesday, Thursday = hidden
  return false;
};

// Compact Navigation Button
const NavButton = ({ to, icon: Icon, label, accent = false }) => (
  <Link 
    to={to}
    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
      accent 
        ? 'bg-red-600 hover:bg-red-700 text-white' 
        : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
    }`}
    data-testid={`nav-btn-${label.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <Icon className="w-4 h-4" />
    <span>{label}</span>
  </Link>
);

// News Article Card (compact list style)
const NewsListItem = ({ article, index }) => {
  const linkUrl = article.redirect_url || `/news/${article.article_id}`;
  
  return (
    <Link 
      to={linkUrl}
      className="flex gap-3 py-3 border-b border-gray-100 last:border-b-0 hover:bg-gray-50 transition-colors group"
      data-testid={`news-item-${index}`}
    >
      {article.image_url && (
        <img 
          src={article.image_url} 
          alt=""
          className="w-16 h-12 sm:w-20 sm:h-14 object-cover rounded flex-shrink-0"
          onError={(e) => { e.target.style.display = 'none'; }}
        />
      )}
      <div className="flex-1 min-w-0">
        <span className="text-[10px] font-bold text-red-600 uppercase tracking-wide">{article.category}</span>
        <h3 className="text-sm font-semibold text-[#00204E] line-clamp-2 leading-snug group-hover:text-red-600 transition-colors">
          {article.title}
        </h3>
      </div>
      <ChevronRight className="w-4 h-4 text-gray-300 flex-shrink-0 self-center" />
    </Link>
  );
};

// Featured Article (hero card) - Homepage Featured Story
const FeaturedArticle = ({ article }) => {
  if (!article) return null;
  const linkUrl = article.redirect_url || `/news/${article.article_id}`;
  
  return (
    <Link 
      to={linkUrl}
      className="block group relative overflow-hidden rounded-lg bg-gray-200"
      data-testid="featured-article"
    >
      <div className="aspect-[16/9] sm:aspect-[2/1]">
        <img 
          src={article.image_url} 
          alt={article.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          onError={(e) => { e.target.src = 'https://via.placeholder.com/640x320?text=SheLovesThee'; }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#00204E]/90 via-[#00204E]/40 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <span className="text-[10px] font-bold text-red-400 uppercase tracking-wide">{article.category}</span>
          <h2 className="text-white font-bold text-base sm:text-lg leading-tight mt-1 group-hover:text-red-400 transition-colors line-clamp-2">
            {article.title}
          </h2>
          {article.subtitle && (
            <p className="text-white/80 text-xs sm:text-sm mt-1 line-clamp-1">{article.subtitle}</p>
          )}
          {article.cta && (
            <span className="inline-flex items-center gap-1 mt-2 bg-red-600 hover:bg-red-700 text-white text-xs font-bold px-3 py-1.5 rounded transition-colors">
              {article.cta} <ChevronRight className="w-3 h-3" />
            </span>
          )}
        </div>
      </div>
    </Link>
  );
};

// Quick Stat Card - Now with dynamic data
const QuickStat = ({ icon: Icon, label, value, color = 'text-[#00204E]' }) => (
  <div className="flex items-center gap-2 bg-white rounded-lg p-3 shadow-sm border border-gray-100" data-testid={`quick-stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <Icon className={`w-5 h-5 ${color}`} />
    <div>
      <div className="text-xs text-gray-500 uppercase tracking-wide">{label}</div>
      <div className="text-sm font-bold text-gray-800">{value}</div>
    </div>
  </div>
);

// Get current week number for display - Week 0 is a valid SWAC football week
const getWeekNumber = () => {
  const seasonStart = new Date('2026-08-25'); // Week 0 starts here
  const now = new Date();
  // No +1 offset - Week 0 is real, so first week after seasonStart is Week 0
  const weekNum = Math.floor((now - seasonStart) / (7 * 24 * 60 * 60 * 1000));
  return weekNum >= 0 ? weekNum : 0;
};

export default function HomePageNew() {
  const [articles, setArticles] = useState([]);
  const [featuredCMSArticle, setFeaturedCMSArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scoreboardSettings, setScoreboardSettings] = useState({ display_mode: 'automatic' });
  const [showScoreboard, setShowScoreboard] = useState(true);
  const [pickemStatus, setPickemStatus] = useState({ week_number: 6, headline_matchups: ['JSU @ GRAM', 'SOU @ PVAM', 'FAMU @ ALST'], game_of_week: { away: 'Jackson State', home: 'Grambling State', kickoff_time: '2026-10-10T17:00:00' } });
  const [gotwVideo, setGotwVideo] = useState({
    video_url: "https://www.youtube.com/embed/Z3cp1GXqizc",
    video_title: "SheLovesThee Game of the Week Live",
    caption: "JSU vs Grambling Coverage",
    link_url: "/swac-pickem",
    link_text: "Make Your Picks"
  });
  const [electionMode, setElectionMode] = useState(false);

  useEffect(() => {
    const refreshMode = () => axios.get(`${BACKEND_URL}/api/election/dashboard`).then(r => setElectionMode(Boolean(r.data?.mode?.is_active))).catch(() => {});
    refreshMode();
    const interval = setInterval(refreshMode, 15000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch regular articles
        const articlesResponse = await axios.get(`${BACKEND_URL}/api/articles`);
        const articlesData = Array.isArray(articlesResponse.data) ? articlesResponse.data : articlesResponse.data.articles || [];
        setArticles(articlesData.slice(0, 12));
        
        // Fetch featured interactive article from CMS
        const cmsResponse = await axios.get(`${BACKEND_URL}/api/interactive-articles?featured=true&limit=1`);
        const cmsArticles = cmsResponse.data?.articles || [];
        const featured = cmsArticles.find(a => a.homepage?.is_featured);
        setFeaturedCMSArticle(featured || null);
        
        // Fetch GOTW video settings
        try {
          const gotwResponse = await axios.get(`${BACKEND_URL}/api/gotw-video`);
          if (gotwResponse.data) {
            setGotwVideo(gotwResponse.data);
          }
        } catch {
          // Use defaults if not set
        }

        try {
          const pickemResponse = await axios.get(`${BACKEND_URL}/api/pickem/public/status`);
          setPickemStatus(pickemResponse.data);
        } catch {
          // Keep the last active-week banner visible if Pick'em status is temporarily unavailable.
        }
        try {
          const electionResponse = await axios.get(`${BACKEND_URL}/api/election/dashboard`);
          setElectionMode(Boolean(electionResponse.data?.mode?.is_active));
        } catch {}
        
        // Fetch scoreboard visibility settings
        try {
          const settingsResponse = await axios.get(`${BACKEND_URL}/api/admin/settings/scoreboard`);
          const settings = settingsResponse.data || { display_mode: 'automatic' };
          setScoreboardSettings(settings);
          setShowScoreboard(shouldShowScoreboard(settings.display_mode));
        } catch {
          // Default to automatic if settings don't exist
          setShowScoreboard(shouldShowScoreboard('automatic'));
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    
    // Re-check visibility every minute (in case we cross the 6 AM boundary)
    const visibilityInterval = setInterval(() => {
      setShowScoreboard(shouldShowScoreboard(scoreboardSettings.display_mode));
    }, 60000);
    
    return () => clearInterval(visibilityInterval);
  }, [scoreboardSettings.display_mode]);

  return (
    <>
      <Helmet>
        <title>SheLovesThee | SWAC Football Scores & HBCU Sports</title>
        <meta name="description" content="Live SWAC football scores, HBCU sports news, Jackson State coverage, and SWAC Pick'em predictions." />
        <link rel="canonical" href="https://shelovesthee.com/" />
        <meta property="og:title" content="SheLovesThee | SWAC Football Scores & HBCU Sports" />
        <meta property="og:description" content="Live SWAC football scores, HBCU sports news, Jackson State coverage, and SWAC Pick'em predictions." />
        <meta property="og:url" content="https://shelovesthee.com/" />
        <meta property="og:image" content="https://customer-assets-39nsmqrw.emergentagent.net/job_hbcu-sports-hub/artifacts/12mdvn7w_9ABE93F3-1197-42CC-98EE-6245B2328755.png" />
      </Helmet>

      <div className="min-h-screen bg-gray-50" data-testid="homepage-new">
        {electionMode && <ElectionHub />}
        {electionMode && <div id="sports" className="bg-[#0b1e36] px-4 py-5 text-center text-white"><p className="text-xs font-bold uppercase tracking-[.2em] text-blue-200">SheLovesThee Podcast</p><h2 className="mt-1 text-3xl font-black">HBCU Sports — Still in the Game</h2><p className="mt-1 text-sm text-slate-200">Scores, Pick’em, recruiting, Coaches Call, Jackson State and QB Analytics.</p></div>}
        {/* Active SWAC Pick'em Promotional Banner */}
        {!electionMode && <>
        <Link 
          to="/swac-pickem"
          className="block bg-gradient-to-r from-[#00204E] via-[#003366] to-[#00204E] py-3 px-4 hover:from-[#001a3d] hover:via-[#002952] hover:to-[#001a3d] transition-all duration-300 border-b-2 border-white"
          data-testid="active-pickem-banner"
        >
          <div className="max-w-6xl mx-auto flex items-center justify-center gap-3 flex-wrap">
            <span className="text-xl animate-bounce">🏈</span>
            <div className="text-center">
              <span className="block text-white font-black text-sm sm:text-base uppercase tracking-wider">
                WEEK {pickemStatus.week_number} SWAC PICK'EM IS OPEN!
              </span>
              <span className="block text-blue-200 font-semibold text-xs sm:text-sm">
                {(pickemStatus.headline_matchups || []).join(' • ')}
              </span>
            </div>
            <span className="bg-white text-[#00204E] font-black px-3 py-1.5 rounded-full text-xs uppercase animate-pulse shadow-lg">
              MAKE YOUR WEEK {pickemStatus.week_number} PICKS
            </span>
          </div>
        </Link>
        </>}

        {/* Game of the Week Banner */}
        {!electionMode && <Link
          to="/swac-pickem"
          className="block bg-gradient-to-r from-[#0b4e9b] via-yellow-500 to-[#8e0e2c] hover:from-[#0d5eb8] hover:via-yellow-400 hover:to-[#a01030] text-white py-2 px-4 transition-colors"
          data-testid="game-of-week-banner"
        >
          <div className="max-w-6xl mx-auto flex items-center justify-center gap-2">
            <Newspaper className="w-4 h-4 animate-pulse" />
            <span className="font-bold text-xs sm:text-sm uppercase tracking-wide">
              ⭐ Game of the Week: {pickemStatus.game_of_week?.away || 'TBD'} vs {pickemStatus.game_of_week?.home || 'TBD'}
            </span>
            <ChevronRight className="w-4 h-4" />
          </div>
        </Link>}

        {/* Main Content Container */}
        <div className="max-w-6xl mx-auto">
          
          {/* SheLovesThee Podcast Logo */}
          <div className="flex justify-center py-3 md:py-4" data-testid="shelovesthee-logo-container">
            <img 
              src="/images/shelovesthee-logo.jpg" 
              alt="SheLovesThee Podcast" 
              className="w-[160px] sm:w-[200px] h-auto object-contain"
            />
          </div>
          
          {/* SCOREBOARD - Conditionally rendered based on schedule/admin settings */}
          {/* VISIBLE: Friday 6AM ET → Monday 6AM ET | HIDDEN: Monday 6AM ET → Friday 6AM ET */}
          {showScoreboard && (
            <div className="px-3 pb-2" data-testid="scoreboard-container">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <HomeScoreboard />
              </div>
            </div>
          )}

          {/* Quick Navigation Row */}
          <div className="px-3 pb-3">
            <div className="flex flex-wrap gap-2">
              <NavButton to="/swac-pickem" icon={Target} label="Pick'em" accent={true} />
              <NavButton to="/qb-analytics" icon={BarChart3} label="QB Analytics" />
              <NavButton to="/gameday" icon={Calendar} label="Gameday" />
              <NavButton to="/fan-zone" icon={Users} label="Fan Zone" />
            </div>
          </div>

          {/* Two Column Layout: Featured + News List */}
          <div className="px-3 pb-6">
            {/* Primary Content Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
              
              {/* Main Content Area - 8 columns on desktop */}
              <div className="lg:col-span-8">
                {/* Quick Stats Row */}
                <div className="grid grid-cols-3 gap-2 mb-4">
                  <QuickStat icon={Trophy} label="Pick'em" value={`Week ${getWeekNumber()} Live`} color="text-red-600" />
                  <QuickStat icon={TrendingUp} label="QB Stats" value="Rankings" color="text-blue-600" />
                  <QuickStat icon={Calendar} label="Gameday" value="SWAC Action" color="text-green-600" />
                </div>
                
                {/* ESPN-Style News Section - Single source of truth for all articles */}
                <ESPNNewsSection />
              </div>
              
              {/* Sidebar - 4 columns on desktop */}
              <div className="lg:col-span-4">
                {/* YouTube Live + Live Chat - Game of the Week */}
                <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
                  <div className="bg-gradient-to-r from-[#0b4e9b] to-[#8e0e2c] px-3 py-2 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Play className="w-4 h-4 text-white animate-pulse" />
                      <span className="text-white text-xs font-bold uppercase tracking-wide">Live Now: Game of the Week</span>
                    </div>
                    <span className="text-red-300 text-[10px] font-semibold animate-pulse">● LIVE</span>
                  </div>
                  <div className="aspect-video">
                    <iframe 
                      width="100%" 
                      height="100%" 
                      src={`${gotwVideo.video_url}?autoplay=0&rel=0`}
                      title={gotwVideo.video_title || "SheLovesThee Game of the Week Live"}
                      frameBorder="0" 
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" 
                      allowFullScreen
                      className="w-full h-full"
                    />
                  </div>
                  <div className="px-3 py-2 bg-gray-50 flex items-center justify-between">
                    <span className="text-xs text-gray-600">{gotwVideo.caption}</span>
                    <Link 
                      to={gotwVideo.link_url || "/swac-pickem"} 
                      className="text-xs text-red-600 hover:text-red-700 font-semibold flex items-center gap-1"
                    >
                      {gotwVideo.link_text || "Make Your Picks"} <ChevronRight className="w-3 h-3" />
                    </Link>
                  </div>
                </div>
                
                {/* Live Chat */}
                <div className="mt-4 h-[350px]">
                  <LiveChat 
                    chatId="jsu-vs-aamu-live-2026" 
                    title="Game Day Chat"
                  />
                </div>
                
                {/* Sidebar Ad */}
                <div className="mt-4" data-testid="sidebar-ad">
                  <AdSenseAd 
                    adSlot="3019687562"
                    label="Advertisement"
                  />
                </div>
              </div>
            </div>
            
            {/* Sidebar Ad - Mobile Only (below main content) */}
            <div className="lg:hidden mt-4" data-testid="sidebar-ad-mobile">
              <AdSenseAd 
                adSlot="3019687562"
                label="Advertisement"
              />
            </div>
          </div>

          {/* Secondary Navigation - Full Width Cards */}
          <div className="px-3 pb-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Link 
                to="/swac-pickem"
                className="bg-gradient-to-br from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 text-white rounded-lg p-4 transition-all hover:shadow-lg group"
                data-testid="card-pickem"
              >
                <Target className="w-6 h-6 mb-2 group-hover:scale-110 transition-transform" />
                <div className="font-bold text-sm">SWAC Pick'em</div>
                <div className="text-[10px] text-red-200 mt-0.5">Make your picks</div>
              </Link>
              
              <Link 
                to="/qb-analytics"
                className="bg-gradient-to-br from-[#00204E] to-[#003366] hover:from-[#003366] hover:to-[#004488] text-white rounded-lg p-4 transition-all hover:shadow-lg group"
                data-testid="card-qb-analytics"
              >
                <BarChart3 className="w-6 h-6 mb-2 group-hover:scale-110 transition-transform" />
                <div className="font-bold text-sm">QB Analytics</div>
                <div className="text-[10px] text-blue-200 mt-0.5">Stats & rankings</div>
              </Link>
              
              <Link 
                to="/gameday"
                className="bg-gradient-to-br from-green-600 to-green-700 hover:from-green-500 hover:to-green-600 text-white rounded-lg p-4 transition-all hover:shadow-lg group"
                data-testid="card-gameday"
              >
                <Calendar className="w-6 h-6 mb-2 group-hover:scale-110 transition-transform" />
                <div className="font-bold text-sm">Gameday</div>
                <div className="text-[10px] text-green-200 mt-0.5">Live updates</div>
              </Link>
              
              <Link 
                to="/fan-zone"
                className="bg-gradient-to-br from-purple-600 to-purple-700 hover:from-purple-500 hover:to-purple-600 text-white rounded-lg p-4 transition-all hover:shadow-lg group"
                data-testid="card-fan-zone"
              >
                <Users className="w-6 h-6 mb-2 group-hover:scale-110 transition-transform" />
                <div className="font-bold text-sm">Fan Zone</div>
                <div className="text-[10px] text-purple-200 mt-0.5">Polls & community</div>
              </Link>
            </div>
          </div>

          {/* Homepage Bottom Ad */}
          <div className="px-3 pb-6" data-testid="homepage-bottom-ad">
            <AdSenseAd 
              adSlot="2800195719"
              label="Advertisement"
            />
          </div>
        </div>
      </div>
    </>
  );
}
